import React from 'react';
import { Box, IconButton, TextField, Typography, Button } from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';

interface PlaybookStringListFieldProps {
  label: string;
  helperText?: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  maxEntries?: number;
}

const DEFAULT_MAX_ENTRIES = 10;

/**
 * An editable list of one-line strings — exemplars, follow-ups, sub-conditions.
 *
 * Exemplars are read ALOUD by the rep mid-call and follow-ups are offered
 * verbatim when a loop opens, so these are product copy rather than
 * configuration. The field keeps them as discrete rows instead of one textarea
 * so an admin cannot accidentally merge two lines into a sentence no one would
 * say out loud.
 */
const PlaybookStringListField: React.FC<PlaybookStringListFieldProps> = ({
  label,
  helperText,
  values,
  onChange,
  placeholder,
  maxEntries = DEFAULT_MAX_ENTRIES
}) => {
  const entries = values ?? [];

  const handleEntryChange = (index: number, next: string) => {
    onChange(entries.map((entry, position) => (position === index ? next : entry)));
  };

  const handleRemove = (index: number) => {
    onChange(entries.filter((_, position) => position !== index));
  };

  const handleAdd = () => {
    if (entries.length >= maxEntries) return;
    onChange([...entries, '']);
  };

  return (
    <Box mt={2}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      {helperText && (
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          {helperText}
        </Typography>
      )}

      {entries.length === 0 && (
        <Typography variant="caption" color="text.disabled" display="block" py={0.5}>
          None yet.
        </Typography>
      )}

      {entries.map((entry, index) => (
        <Box key={index} display="flex" alignItems="center" gap={1} mt={0.5}>
          <TextField
            fullWidth
            size="small"
            value={entry ?? ''}
            placeholder={placeholder}
            onChange={(event) => handleEntryChange(index, event.target.value)}
            inputProps={{ style: { fontSize: 13 } }}
          />
          <IconButton
            size="small"
            aria-label={`Remove ${label} entry ${index + 1}`}
            onClick={() => handleRemove(index)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      {entries.length < maxEntries && (
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 0.5 }}>
          Add
        </Button>
      )}
    </Box>
  );
};

export default PlaybookStringListField;
